import { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { TRIVELTA_PRODUCTS } from "../../../utils/products";

import EventMarkets from "./EventMarkets/EventMarkets";
import AllSports from "./AllSports/AllSports";
import ShortcutsContainer from "./Shortcuts/ShortcutsContainer";

import "./stylesSportsbookFM.css";
import "./Shortcuts/stylesShortcuts.css";
import "../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
import FeatureFlagGuard from "../../FeatureFlagGuard/FeatureFlagGuard";
import { AppStrings } from "../../../utils/constants/AppStrings";
import PopularTabs from "./PopularTabs/PopularTabs";
import UpcomingTabs from "./UpcomingTabs/UpcomingTabs";
import TopMarkets from "./TopMarkets/TopMarkets";
import TopLeaguesPerSport from "./TopLeaguesPerSport/TopLeaguesPerSport";
import Futures from "./Futures/Futures";
import LiveSportsV2 from "./LiveSportsV2/LiveSportsV2";
import { SportsbookFmProvider } from "./SportsbookFmContext";
import { showTranslatedNamesNotice } from "./translatedNamesNotice";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

const TAB_PARAM_MAP = {
  "Live SportsV2": "live-sportsv2",
  "Event Markets": "event-markets",
  "All Sports": "all-sports",
  "ShortcutsV2": "shortcutsv2",
  "Popular Tabs": "popular-tabs",
  "Upcoming Tabs": "upcoming-tabs",
  "Top Markets": "top-markets",
  "Top Leagues Per Sport": "top-leagues-per-sport",
  "Futures": "futures",
};

const PARAM_TAB_MAP = Object.fromEntries(
  Object.entries(TAB_PARAM_MAP).map(([tab, param]) => [param, tab])
);

function SportsbookFMContent() {
  usePageTitle(AppStrings.SPORTSBOOK_FM_PAGE_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("All Sports");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_FM");
    showTranslatedNamesNotice();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab initialization and URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");

    if (tabParam) {
      const mappedTab = PARAM_TAB_MAP[tabParam];
      if (mappedTab) {
        setActiveTab(mappedTab);
      } else {
        // Fallback for unexpected/legacy values in URL.
        const normalizedTab = tabParam
          .replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        setActiveTab(normalizedTab);
      }
    }
  }, [location.search, setActiveSidebarTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    const tabParam =
      TAB_PARAM_MAP[tabName] || tabName.toLowerCase().replace(/\s+/g, "-");
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", tabParam);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  return (
    <>
      <div className="location-restrictions-table-container">
        <div className="location-restrictions-table">
          <>
            {/* TODO(PAMENG-1820): drop `feature="sportsbook_v2"` from every
                guard in this strip once BE exposes `sportsbook` everywhere. */}
            <div className="dashboard-tabs">
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Live SportsV2" ? "active" : null}
                  onClick={() => handleTabChange("Live SportsV2")}
                >
                  {AppStrings.LIVE_SPORTS_V2}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Event Markets" ? "active" : null}
                  onClick={() => setActiveTab("Event Markets")}
                >
                  {AppStrings.EVENT_MARKETS}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "All Sports" ? "active" : null}
                  onClick={() => setActiveTab("All Sports")}
                >
                  {AppStrings.ALL_SPORTS}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "ShortcutsV2" ? "active" : null}
                  onClick={() => handleTabChange("ShortcutsV2")}
                >
                  {AppStrings.SPORTSBOOK_FM_SHORTCUTS_TAB}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Popular Tabs" ? "active" : null}
                  onClick={() => handleTabChange("Popular Tabs")}
                >
                  {AppStrings.POPULAR_TABS}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Upcoming Tabs" ? "active" : null}
                  onClick={() => handleTabChange("Upcoming Tabs")}
                >
                  {AppStrings.UPCOMING_TABS}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Top Markets" ? "active" : null}
                  onClick={() => handleTabChange("Top Markets")}
                >
                  {AppStrings.TOP_MARKETS}
                </button>
              </FeatureFlagGuard> 
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Top Leagues Per Sport" ? "active" : null}
                  onClick={() => handleTabChange("Top Leagues Per Sport")}
                >
                  {AppStrings.TOP_LEAGUES_PER_SPORT}
                </button>
              </FeatureFlagGuard> 
              <FeatureFlagGuard
                product={TRIVELTA_PRODUCTS.SPORTSBOOK}
                feature="sportsbook_v2"
                mode="conditional"
              >
                <button
                  className={activeTab === "Futures" ? "active" : null}
                  onClick={() => handleTabChange("Futures")}
                >
                  {AppStrings.FUTURES}
                </button>
              </FeatureFlagGuard>
            </div>
            <div className="tab-content-sportsbook-fm ">
              {activeTab === "Live SportsV2" && <LiveSportsV2 />}
              {activeTab === "Event Markets" && <EventMarkets />}
              {activeTab === "All Sports" && <AllSports />}
              {activeTab === "ShortcutsV2" && <ShortcutsContainer shortcutsVersion="v2"/>}
              {activeTab === "Popular Tabs" && <PopularTabs />}
              {activeTab === "Upcoming Tabs" && <UpcomingTabs />}
              {activeTab === "Top Markets" && <TopMarkets />}
              {activeTab === "Top Leagues Per Sport" && <TopLeaguesPerSport />}
              {activeTab === "Futures" && <Futures />}
            </div>
          </>
        </div>
      </div>
    </>
  );
}

function SportsbookFM() {
  return (
    <SportsbookFmProvider>
      <SportsbookFMContent />
    </SportsbookFmProvider>
  );
}

export default SportsbookFM;
